<template>
  <div class="w-full h-full overflow-hidden grid grid-cols-[1fr]">
    <div class="overflow-hidden grid grid-rows-[1fr]">
      <VanTabs
        class="w-full overflow-hidden grid grid-rows-[auto_1fr] all-tabs"
        v-model:active="step"
        :ellipsis="false"
        line-width="20"
        line-height="2"
      >
        <VanTab
          v-if="showTabs.includes('people')"
          name="people"
          :title="t(`painting.people.title`)"
          class="after:pt-10"
        >
          <button
            class="btn btn-sm btn-primary my-2 ml-4"
            @click="clickSwitchMode"
          >
            {{
              !isMutiMode
                ? t(`painting.switchMuti`)
                : t(`painting.switchSingle`)
            }}
          </button>

          <ImageTabsTrait v-if="!isMutiMode" :req-data="reqData" />
          <template v-else>
            <div class="mt-4 mb-2 px-4 flex items-center justify-between">
              <div class="flex items-center">
                <i class="iconfont icon-pray mr-1"></i>
                <span class="font-bold">
                  {{ t(`painting.people.poses`) }}
                </span>
              </div>
            </div>

            <div
              class="grid grid-cols-[1fr_auto] max-w-[100vw] overflow-hidden pr-4 mb-4 border border-transparent pb-2"
            >
              <div
                class="flex-1 flex gap-2 px-4 whitespace-nowrap overflow-hidden mr-4"
              >
                <div
                  v-for="c in ['stand by stand', 'kiss', 'hug']"
                  :key="c"
                  @click="clickPoses(c)"
                  :class="[
                    'badge badge-neutral cursor-pointer',
                     reqData.poses!.includes(c) ? 'badge-primary' : 'badge-neutral',
                  ]"
                >
                  {{ t(`painting.poses.${c}`) }}
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-1 px-4">
              <VanTag
                v-for="(mate, idx) in reqData.mates"
                :plain="activeMate?.id !== mate.id"
                :type="activeMate?.id === mate.id ? 'primary' : 'default'"
                closeable
                @click="clickSwitchMate(mate)"
                @close="clickRemoveMate(mate, idx)"
                class="p-1.5 cursor-pointer"
              >
                {{
                  traitStore.traitMap[mate.id!].name ||
                  t(`SidebarNavi.role`) + " " + idx
                }}
              </VanTag>
              <VanTag
                plain
                @click="showAddMate = true"
                class="p-1.5 cursor-pointer"
              >
                {{ t(`painting.muti.addMate`) }}
              </VanTag>
            </div>
            <ImageTabsTraitMuti
              v-if="activeMate"
              :key="activeMate.id"
              :mate="activeMate"
              :req-data="reqData"
            />
          </template>
          <div class="pt-10"></div>
        </VanTab>
        <VanTab
          v-if="showTabs.includes('layout')"
          name="layout"
          :title="t(`painting.layout.title`)"
        >
          <ImageTabsLayout
            :req-data="reqData"
            @upload-nsfw-image="($event) => emit('uploadNsfwImage', $event)"
          />
        </VanTab>
        <VanTab
          v-if="showTabs.includes('origin')"
          name="origin"
          :title="t(`painting.origin.title`)"
        >
          <ImageTabsLayout
            :req-data="reqData"
            is-origin
            @upload-nsfw-image="($event) => emit('uploadNsfwImage', $event)"
          />
        </VanTab>
        <VanTab
          v-if="showTabs.includes('env')"
          name="env"
          :title="t(`painting.env.title`)"
        >
          <EditPromptsContainer
            mode="env"
            v-model:prompt="reqData.env.prompt"
            v-model:negative-prompt="reqData.env.negativePrompt"
            :has-preview="false"
            :has-modal="false"
          />
        </VanTab>
        <VanTab
          v-if="showTabs.includes('style')"
          name="style"
          :title="t(`painting.style.title`)"
        >
          <ImageTabsStyle
            v-model:imageStyle="reqData.style"
            v-model:mix-style="reqData.mixStyle"
            v-model:custom-style="reqData.customStyle"
          />
        </VanTab>
        <VanTab
          v-if="showTabs.includes('setting')"
          name="setting"
          :title="t(`painting.setting`)"
        >
          <ImageTabsSetting :req-data="reqData" />
        </VanTab>
      </VanTabs>
    </div>
  </div>
  <ImageModalAddMate
    v-if="reqData.mates"
    v-model:show="showAddMate"
    @select-trait="onAddTrait"
    :filter-ids="reqData.mates.map((v) => v.id)"
  />
</template>

<script lang="ts" setup>
import { getInitTraitPromptJson } from "@/modules/common";
import { showConfirmDialog } from "vant";
import { PropType } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { initStyle } from "./initStyle";
import { onMounted } from "vue";
import { useMateTrait } from "@/stores/mateTrait";
import { StorageGet, StorageSet } from "@/tools/localStorage";

const props = defineProps({
  reqData: {
    type: Object as PropType<PromptJson>,
    required: true,
  },
  showTabs: {
    type: Object as PropType<string[]>,
    default: () => ["people", "layout", "env", "style", "setting"],
  },
  isMutiMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:isMutiMode", "uploadNsfwImage"]);

const step = ref("people");
const { t } = useI18n();
const traitStore = useMateTrait();
// muti mode
const clickSwitchMode = async () => {
  let isMutiMode = !props.isMutiMode;
  if (isMutiMode) {
    if (!StorageGet("Message.mutiUserTip")) {
      try {
        await showConfirmDialog({
          message: t(`Message.mutiUserTip`),
          confirmButtonText: t("Buttons.confirm"),
          cancelButtonText: t("Buttons.cancel"),
        });
        StorageSet("Message.mutiUserTip", "1");
      } catch (e) {
        return;
      }
    }
  }

  emit("update:isMutiMode", isMutiMode);
  props.reqData.ratio = "16:9";
  if ((props.reqData.mates?.length || 0) > 0 && !activeMate.value)
    activeMate.value = props.reqData.mates![0];
  if (!props.reqData.poses) props.reqData.poses = ["stand by stand"];
};

const clickPoses = (pose: string) => {
  if (!props.reqData.poses) props.reqData.poses = [];
  if (props.reqData.poses.includes(pose)) {
    props.reqData.poses = props.reqData.poses.filter((v) => v !== pose);
  } else {
    props.reqData.poses.push(pose);
  }
};

// mate...
const showAddMate = ref(false);
const activeMate = ref<PromptJsonMateTrait>();
const clickSwitchMate = (trait: PromptJsonMateTrait) => {
  activeMate.value = trait;
};

const clickRemoveMate = async (trait: PromptJsonMateTrait, idx: number) => {
  try {
    await showConfirmDialog({
      message: t(`Message.deleteConfirm`),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch (e) {
    return;
  }
  props.reqData.mates!.splice(idx, 1);
  if (activeMate.value?.id === trait.id) {
    activeMate.value = props.reqData.mates![0];
  }
};

const onAddTrait = (trait: MateTrait) => {
  const { prompt, id, style, mixStyle, customStyle } = trait;
  props.reqData.mates!.push({
    id,
    prompt,
    ...getInitTraitPromptJson(),
  });
  activeMate.value = props.reqData.mates![props.reqData.mates!.length - 1];
  initStyle(props.reqData, style, customStyle, mixStyle);
};

const showMutiMode = ref(true);

// watch(
//   () => props.reqData.isUnlimited,
//   async (val) => {
//     if (val) {
//       const { isMutiMode, reqData } = props;
//       if (isMutiMode) emit("update:isMutiMode", false);
//       if (reqData.mateTraitId) {
//         const trait = await traitStore.getOrFetchTraitItem(reqData.mateTraitId);
//         if (!trait.customStyle?.version)
//           reqData.customStyle = { version: 1, prompt: "" };
//       }
//       showMutiMode.value = false;
//     } else {
//       showMutiMode.value = true;
//     }
//   }
// );

onMounted(() => {
  if (props.reqData.mates && props.reqData.mates.length > 0) {
    activeMate.value = props.reqData.mates![0];
  }
  if (props.reqData.isUnlimited) showMutiMode.value = false;
});
</script>

<style scoped>
:deep(.all-tabs .van-tabs__content) {
  overflow: auto;
}

:deep(.van-tabs__nav .van-tab__text) {
  text-overflow: unset;
}
</style>
